// Le moteur de parcours, en fonctions pures. Aucun accès réseau, aucun état
// React : c'est la partie qu'on peut tester sans lancer l'application.
//
// Linéaire souple (P2) : on avance, on peut sauter, l'étape sautée reste ouverte
// et revient. Les étapes mises de côté reviennent après l'étape 11, avant le
// bilan — ce sont des dettes, pas des non-applicabilités.

import type { Etape, StatutEtape, CodeBranche, EtatBranche } from './types'
import { REPRISE_SEUIL_MS } from './steps'

export const estValidee = (s: StatutEtape) => s === 'validee'
export const estSkip = (s: StatutEtape) => s === 'skip_regle' || s === 'skip_question'
export const estDeCote = (s: StatutEtape) => s === 'de_cote'
export const estActionnable = (s: StatutEtape) => s === 'a_faire' || s === 'en_cours'
/** Soldée = elle ne reviendra pas d'elle-même dans le parcours. */
export const estSoldee = (s: StatutEtape) => estValidee(s) || estSkip(s)

export const parRang = (a: Etape, b: Etape) => a.rang - b.rang

/**
 * Où le parcours emmène après avoir soldé l'étape `code`.
 *
 * 1. l'étape actionnable suivante ;
 * 2. sinon une étape actionnable laissée en arrière (le saut en avant est
 *    autorisé, mais rien ne se perd) ;
 * 3. sinon les mises de côté, qui reviennent en fin de parcours ;
 * 4. sinon le bilan.
 */
export function prochaineEtape(etapes: Etape[], code: string): string | 'bilan' {
  const liste = [...etapes].sort(parRang)
  const i = liste.findIndex((e) => e.code === code)
  const depuis = i === -1 ? -1 : i

  const apres = liste.slice(depuis + 1).find((e) => estActionnable(e.statut))
  if (apres) return apres.code

  const avant = liste.find((e) => estActionnable(e.statut) && e.code !== code)
  if (avant) return avant.code

  const deCote = liste.find((e) => estDeCote(e.statut))
  if (deCote) return deCote.code

  return 'bilan'
}

/** Reste-t-il quelque chose à faire avant de pouvoir solder la journée ? */
export function restantes(etapes: Etape[]): Etape[] {
  return [...etapes]
    .sort(parRang)
    .filter((e) => estActionnable(e.statut) || estDeCote(e.statut))
}

export interface Progression {
  total: number
  validees: number
  naRegle: number
  naQuestion: number
  deCote: number
  aFaire: number
  forcees: number
}

export function progression(etapes: Etape[]): Progression {
  const p: Progression = {
    total: etapes.length, validees: 0, naRegle: 0, naQuestion: 0,
    deCote: 0, aFaire: 0, forcees: 0,
  }
  for (const e of etapes) {
    if (e.statut === 'validee') p.validees++
    else if (e.statut === 'skip_regle') p.naRegle++
    else if (e.statut === 'skip_question') p.naQuestion++
    else if (e.statut === 'de_cote') p.deCote++
    else p.aFaire++
    if (e.force_valide) p.forcees++
  }
  return p
}

export type Segment = 'done' | 'skip' | 'park' | 'cur' | 'todo'

export function segment(e: Etape, codeCourant: string | null): Segment {
  if (e.statut === 'validee') return 'done'
  if (estSkip(e.statut)) return 'skip'
  if (e.statut === 'de_cote') return 'park'
  if (e.code === codeCourant) return 'cur'
  return 'todo'
}

// ── L'aiguillage de l'étape 05 ─────────────────────────────────────────────

export interface EtatAiguillage {
  faites: number
  total: number
  prochaine: CodeBranche | null
  branches: Record<CodeBranche, EtatBranche>
}

const BRANCHE_VIDE: EtatBranche = { statut: 'a_faire' }

export function etatAiguillage(etape: Etape | undefined): EtatAiguillage {
  const dep = etape?.branche?.dep ?? BRANCHE_VIDE
  const sco = etape?.branche?.sco ?? BRANCHE_VIDE
  const soldee = (b: EtatBranche) => b.statut === 'validee' || b.statut === 'skip_question'
  const faites = [dep, sco].filter(soldee).length
  const prochaine: CodeBranche | null =
    !soldee(dep) ? 'dep' : !soldee(sco) ? 'sco' : null
  return { faites, total: 2, prochaine, branches: { dep, sco } }
}

// ── Reprise après interruption (§9.1) ──────────────────────────────────────

export interface Reprise {
  absenceMs: number
  depuis: string
}

/**
 * Reconstruite automatiquement depuis la dernière activité — aucune note à
 * écrire à la main, tu ne la ferais jamais dans le feu de l'action.
 * En dessous de dix minutes d'absence, c'est du bruit : on n'affiche rien.
 */
export function reprise(etape: Etape | undefined, maintenant = Date.now()): Reprise | null {
  if (!etape?.derniere_activite) return null
  if (!estActionnable(etape.statut) && !estDeCote(etape.statut)) return null
  const t = new Date(etape.derniere_activite).getTime()
  const absenceMs = maintenant - t
  if (!Number.isFinite(absenceMs) || absenceMs < REPRISE_SEUIL_MS) return null
  return { absenceMs, depuis: etape.derniere_activite }
}

// ── Le verrou, vu du client ────────────────────────────────────────────────

export type EtatVerrou =
  | { etat: 'libre' }
  | { etat: 'moi' }
  | { etat: 'occupee'; par: string; depuis: string }

export function etatVerrou(
  run: { lock_user: string | null; lock_nom: string | null; lock_at: string | null; lock_expire: string | null } | null,
  moi: string | null,
  maintenant = Date.now(),
): EtatVerrou {
  if (!run?.lock_user) return { etat: 'libre' }
  const expire = run.lock_expire ? new Date(run.lock_expire).getTime() : 0
  if (expire <= maintenant) return { etat: 'libre' }
  if (run.lock_user === moi) return { etat: 'moi' }
  return { etat: 'occupee', par: run.lock_nom ?? 'un autre opérateur', depuis: run.lock_at ?? '' }
}
